import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routes } from '@/lib/routes';
import { Plus, MessageCircle, ChevronRight, X, Users, Compass, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { useChatStore } from '@/store/useChatStore';
import { useTeamStore } from '@/store/useTeamStore';
import { toast } from 'sonner';
import type { ChatSession } from '@/types';

export function ExplorationList() {
  const navigate = useNavigate();
  const { sessions, createSession, setActiveSession, deleteSession } = useChatStore();
  const members = useTeamStore((s) => s.members);

  const [fabOpen, setFabOpen] = useState(false);
  const [showMemberPicker, setShowMemberPicker] = useState(false);

  const allSessions = [...sessions].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

  function startNewExploration() {
    setFabOpen(false);
    const session = createSession('discovery');
    setActiveSession(session.id);
    navigate(routes.innerTeam.discoveryChat(session.id));
  }

  function startMemberConsult(memberId: string) {
    setShowMemberPicker(false);
    setFabOpen(false);
    const session = createSession('member', [memberId]);
    setActiveSession(session.id);
    navigate(routes.innerTeam.memberChat(session.id));
  }

  function startTeamConsult() {
    setFabOpen(false);
    const session = createSession('consultation', members.map((m) => m.id));
    setActiveSession(session.id);
    navigate(routes.innerTeam.teamChat(session.id));
  }

  function onConsultClick() {
    if (members.length === 0) {
      toast.info('Entdecke zuerst ein Teammitglied, bevor du es befragen kannst.');
      setFabOpen(false);
      return;
    }
    if (members.length === 1) {
      startMemberConsult(members[0].id);
      return;
    }
    setShowMemberPicker(true);
  }

  function openSession(session: ChatSession) {
    setActiveSession(session.id);
    if (session.type === 'member') {
      navigate(routes.innerTeam.memberChat(session.id));
    } else if (session.type === 'consultation') {
      navigate(routes.innerTeam.teamChat(session.id));
    } else {
      navigate(routes.innerTeam.discoveryChat(session.id));
    }
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  return (
    <AppShell>
      <PageHeader showBrand />
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {allSessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageCircle size={28} className="text-primary" />
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Noch keine Gespräche</p>
              <p className="text-sm text-muted-foreground max-w-xs">
                Starte eine Erkundung oder befrage ein Mitglied deines inneren Teams.
              </p>
            </div>
          </div>
        ) : (
          allSessions.map((session) => {
            const isMember = session.type === 'member';
            const isTeam = session.type === 'consultation';
            const consultedMember = isMember
              ? members.find((m) => m.id === session.memberIds[0])
              : undefined;
            const sessionMembers = (isMember || isTeam)
              ? members.filter((m) => session.memberIds.includes(m.id))
              : [];
            const discoveredMembers = !isMember && !isTeam
              ? members.filter((m) => session.memberIds.includes(m.id))
              : [];
            const displayTitle = session.title
              || (isMember ? `Gespräch mit ${consultedMember?.name ?? 'Unbekannt'}` : null)
              || (isTeam ? 'Team-Konsultation' : null)
              || 'Neue Erkundung';
            const msgCount = session.messages.filter((m) => m.role === 'user').length;

            return (
              <div
                key={session.id}
                className="w-full rounded-2xl bg-card border border-border flex items-center hover:bg-muted/40 transition-colors"
              >
                <button
                  onClick={() => openSession(session)}
                  className="flex-1 min-w-0 text-left p-4 flex items-center gap-3"
                >
                  {isTeam ? (
                    <div className="flex items-center -space-x-2 shrink-0">
                      {sessionMembers.slice(0, 3).map((m) => (
                        <CharacterPortrait
                          key={m.id}
                          emoji={m.avatarEmoji}
                          color={m.avatarColor}
                          avatarImageUrl={m.avatarImageUrl}
                          size="sm"
                        />
                      ))}
                    </div>
                  ) : isMember && consultedMember ? (
                    <CharacterPortrait
                      emoji={consultedMember.avatarEmoji}
                      color={consultedMember.avatarColor}
                      avatarImageUrl={consultedMember.avatarImageUrl}
                      size="sm"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <MessageCircle size={18} className="text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{displayTitle}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isTeam && <span className="text-primary/70 font-medium">Team-Konsultation · </span>}
                      {isMember && <span className="text-primary/70 font-medium">Konsultation · </span>}
                      {formatDate(session.updatedAt)}
                      {msgCount > 0 && ` · ${msgCount} Nachrichten`}
                    </p>
                    {discoveredMembers.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        {discoveredMembers.slice(0, 4).map((m) => (
                          <CharacterPortrait
                            key={m.id}
                            emoji={m.avatarEmoji}
                            color={m.avatarColor}
                            avatarImageUrl={m.avatarImageUrl}
                            size="sm"
                          />
                        ))}
                        {discoveredMembers.length > 4 && (
                          <span className="text-xs text-muted-foreground ml-1">
                            +{discoveredMembers.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                </button>
                <button
                  onClick={() => {
                    deleteSession(session.id);
                    toast.success('Gespräch gelöscht.');
                  }}
                  className="px-4 py-4 text-muted-foreground hover:text-destructive transition-colors shrink-0"
                  aria-label="Gespräch löschen"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* FAB overlay to close */}
      {fabOpen && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setFabOpen(false)}
        />
      )}

      {/* FAB speed-dial */}
      <div
        className="fixed right-4 flex flex-col items-end gap-3 z-30"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }}
      >
        {fabOpen && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium bg-background/95 border border-border rounded-lg px-3 py-1.5 shadow-sm">
                Neue Erkundung
              </span>
              <button
                onClick={startNewExploration}
                className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center hover:bg-primary/90 transition-colors"
                aria-label="Neue Erkundung"
              >
                <Compass size={20} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium bg-background/95 border border-border rounded-lg px-3 py-1.5 shadow-sm ${members.length < 2 ? 'opacity-40' : ''}`}>
                Team befragen
              </span>
              <button
                onClick={() => {
                  if (members.length < 2) {
                    toast.info('Du brauchst mindestens 2 Teammitglieder für eine Team-Konsultation.');
                    return;
                  }
                  startTeamConsult();
                }}
                disabled={members.length < 2}
                className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Team befragen"
              >
                <Users size={20} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium bg-background/95 border border-border rounded-lg px-3 py-1.5 shadow-sm">
                Mitglied befragen
              </span>
              <button
                onClick={onConsultClick}
                className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center hover:bg-primary/90 transition-colors"
                aria-label="Mitglied befragen"
              >
                <Users size={20} />
              </button>
            </div>
          </>
        )}
        <button
          onClick={() => setFabOpen((o) => !o)}
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all duration-200"
          aria-label="Neues Gespräch"
        >
          {fabOpen ? <X size={24} /> : <Plus size={24} />}
        </button>
      </div>

      {/* Member picker bottom sheet */}
      {showMemberPicker && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowMemberPicker(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-background rounded-t-2xl shadow-xl p-4" style={{ paddingBottom: 'calc(4rem + env(safe-area-inset-bottom) + 1rem)' }}>
            <div className="w-10 h-1 rounded-full bg-border mx-auto mb-4" />
            <p className="text-sm font-semibold mb-3">Welches Mitglied möchtest du befragen?</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {members.map((m) => (
                <button
                  key={m.id}
                  onClick={() => startMemberConsult(m.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors text-left"
                >
                  <CharacterPortrait
                    emoji={m.avatarEmoji}
                    color={m.avatarColor}
                    avatarImageUrl={m.avatarImageUrl}
                    size="sm"
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{m.role}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </AppShell>
  );
}
